import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaSave, FaWrench, FaCalendarAlt } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loading from "../../../components/Loading";
import BackButton from "../../../components/BackButton";
import axios from "axios";
import myConfig from "../../../configs/config";
import { withTranslation } from "react-i18next";

function MaintenanceDetail({ t }) {
  const { farm_id, machine_id, maintenance_id } = useParams();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const [machineData, setMachineData] = useState(null);
  const [currentKm, setCurrentKm] = useState("");
  const [currentHours, setCurrentHours] = useState("");

  // Load data
  useEffect(() => {
    loadMaintenanceData();
    loadMachineData();
  }, [maintenance_id, machine_id]);

  const loadMaintenanceData = async () => {
    try {
      const response = await axios.get(
        `${myConfig.API_URL}/scheduled_maintenance/${maintenance_id}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );
      setMaintenance(response.data);
    } catch (error) {
      console.error("Error loading maintenance data:", error);
      toast.error("Erro ao carregar dados da manutenção");
    }
  };

  const loadMachineData = async () => {
    try {
      const response = await axios.get(
        `${myConfig.API_URL}/equipment/${machine_id}/metrics`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );
      setMachineData(response.data);
      // Set current values if available
      if (response.data.total_distance_km) {
        setCurrentKm(response.data.total_distance_km.toString());
      }
      if (response.data.hour_meter) {
        let meter_in_hours = response.data.hour_meter / 3600000; // Convert milliseconds to hours
        setCurrentHours(meter_in_hours.toString());
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading machine data:", error);
      toast.error("Erro ao carregar dados da máquina");
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update machine's current meter data
      const updateData = {};
      if (currentKm) {
        updateData.total_distance_km = parseInt(currentKm);
      }
      if (currentHours) {
        updateData.hour_meter = parseInt(currentHours);
      }

      await axios.patch(
        `${myConfig.API_URL}/equipment/${machine_id}/update_latest_metrics/`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          },
        }
      );

      toast.success("Dados atualizados com sucesso!");
      // Reload data to show updated calculations
      loadMachineData();
    } catch (error) {
      console.error("Error updating data:", error);
      toast.error("Erro ao atualizar dados");
    } finally {
      setSaving(false);
    }
  };

  const calculateRemaining = () => {
    if (!maintenance || !machineData) return null;

    const calculations = {};

    // Calculate remaining for KM-based maintenance
    if (
      maintenance.next_due_km &&
      maintenance.meter_type === "km" &&
      currentKm
    ) {
      const remaining = maintenance.next_due_km - parseInt(currentKm);
      calculations.km = {
        remaining: remaining.toFixed(2),
        percentage: Math.max(0, (remaining / maintenance.next_due_km) * 100),
        overdue: remaining < 0,
      };
    }

    // Calculate remaining for hours-based maintenance
    if (
      maintenance.next_due_hours &&
      maintenance.meter_type === "h" &&
      currentHours
    ) {
      const nextDueHoursInHours = parseInt(
        maintenance.next_due_hours / 3600000
      );
      const remaining = nextDueHoursInHours - parseInt(currentHours);
      calculations.hours = {
        remaining: remaining,
        percentage: Math.max(0, (remaining / nextDueHoursInHours) * 100),
        overdue: remaining < 0,
      };
    }

    return calculations;
  };

  const formatStatus = (status) => {
    const statusMap = {
      scheduled: { label: "Agendada", class: "badge-info" },
      pending: { label: "Pendente", class: "badge-warning" },
      completed: { label: "Concluída", class: "badge-success" },
    };
    return statusMap[status] || { label: status, class: "badge-secondary" };
  };

  if (loading) {
    return <Loading />;
  }

  if (!maintenance || !machineData) {
    return (
      <div className="container mt-4">
        <div className="mb-3">
          <BackButton label={t("Voltar")} />
        </div>
        <div className="alert alert-danger">Dados não encontrados</div>
      </div>
    );
  }

  const remaining = calculateRemaining();
  const statusInfo = formatStatus(maintenance.status);

  return (
    <div className="container mt-4">
      <div className="mb-3">
        <BackButton label={t("Voltar")} />
      </div>
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="maintenance-header">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-0">{maintenance.name}</h2>
                <h3 className="text-muted">
                  {t("Máquina")}: {maintenance.equipment_name}
                </h3>
              </div>
              <span className={`badge ${statusInfo.class} badge-lg`}>
                {t(statusInfo.label)}
              </span>
            </div>
          </div>
          {/* Main Content */}
          <div className="row">
            {/* Current Meter Values */}
            <div className="col-lg-6 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <FaWrench className="mr-2" />
                    {t("Medidores Atuais")}
                  </h5>
                </div>
                <div className="card-body">
                  <form>
                    <div className="form-group">
                      <label htmlFor="currentKm" className="font-weight-bold">
                        {t("Quilômetros Atuais")}
                      </label>
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control"
                          id="currentKm"
                          value={currentKm}
                          onChange={(e) => setCurrentKm(e.target.value)}
                          placeholder="0"
                        />
                        <div className="input-group-append">
                          <span className="input-group-text">km</span>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="currentHours"
                        className="font-weight-bold"
                      >
                        {t("Horas Atuais")}
                      </label>
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control"
                          id="currentHours"
                          value={currentHours}
                          onChange={(e) => setCurrentHours(e.target.value)}
                          placeholder="0"
                        />
                        <span className="input-group-text">h</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : (
                        <FaSave className="mr-2" />
                      )}
                      {saving ? t("Salvando...") : t("Salvar")}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Maintenance Information & Remaining */}
            <div className="col-lg-6 mb-4">
              <div className="card h-100">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    <FaCalendarAlt className="mr-2" />
                    {t("Informações da Manutenção")}
                  </h5>
                </div>
                <div className="card-body">
                  {/* Maintenance Details */}
                  <div className="mb-4">
                    <h6 className="font-weight-bold">{t("Detalhes")}</h6>
                    {maintenance.description && (
                      <p className="mb-2">
                        <strong>{t("Descrição")}:</strong>{" "}
                        {maintenance.description}
                      </p>
                    )}
                    {maintenance.next_due_km && (
                      <p className="mb-2">
                        <strong>
                          {t("Kilometragem da próxima manutenção")}:
                        </strong>{" "}
                        {maintenance.next_due_km.toFixed(2)} {t("km")}
                      </p>
                    )}

                    {maintenance.next_due_hours && (
                      <p className="mb-2">
                        <strong>{t("Horímetro da próxima manutenção")}:</strong>{" "}
                        {(maintenance.next_due_hours / 3600000).toFixed(2)}{" "}
                        {t("horas")}
                      </p>
                    )}
                    {maintenance.created_by_name && (
                      <p className="mb-2">
                        <strong>{t("Criado por")}:</strong>{" "}
                        {maintenance.created_by_name}
                      </p>
                    )}
                  </div>
                  {/* Remaining Calculations */}
                  {remaining && Object.keys(remaining).length > 0 && (
                    <div className="progress-section">
                      <h6 className="font-weight-bold mb-3">
                        {t("Restante para Manutenção")}
                      </h6>

                      {remaining.km && (
                        <div className="progress-item">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="font-weight-medium">
                              {t("Quilômetros")}:
                            </span>
                            <span
                              className={`font-weight-bold ${
                                remaining.km.overdue
                                  ? "text-danger"
                                  : "text-success"
                              }`}
                            >
                              {remaining.km.overdue
                                ? `${Math.abs(remaining.km.remaining)} km ${t(
                                    "atrasado"
                                  )}`
                                : `${remaining.km.remaining} km`}
                            </span>
                          </div>
                          <div className="progress">
                            <div
                              className={`progress-bar ${
                                remaining.km.overdue
                                  ? "bg-danger"
                                  : "bg-success"
                              }`}
                              role="progressbar"
                              style={{
                                width: `${Math.min(
                                  100,
                                  remaining.km.percentage
                                )}%`,
                              }}
                              aria-valuenow={remaining.km.percentage}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                        </div>
                      )}

                      {remaining.hours && (
                        <div className="progress-item">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="font-weight-medium">
                              {t("Horas")}:{" "}
                            </span>
                            <span
                              className={`font-weight-bold ${
                                remaining.hours.overdue
                                  ? "text-danger"
                                  : "text-success"
                              }`}
                            >
                              {remaining.hours.overdue
                                ? `${Math.abs(remaining.hours.remaining)} h ${t(
                                    "atrasado"
                                  )}`
                                : `${remaining.hours.remaining} h`}
                            </span>
                          </div>
                          <div className="progress">
                            <div
                              className={`progress-bar ${
                                remaining.hours.overdue
                                  ? "bg-danger"
                                  : "bg-success"
                              }`}
                              role="progressbar"
                              style={{
                                width: `${Math.min(
                                  100,
                                  remaining.hours.percentage
                                )}%`,
                              }}
                              aria-valuenow={remaining.hours.percentage}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {(!remaining || Object.keys(remaining).length === 0) && (
                    <div className="alert alert-info">
                      {t(
                        "Esta manutenção não possui medidores configurados ou os valores atuais não foram informados."
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>{" "}
      <ToastContainer position="bottom-center" />
      <style jsx="true">{`
        .container {
          background-color: #f8f9fa;
          min-height: 100vh;
          padding-top: 20px;
          padding-bottom: 40px;
        }

        .badge-lg {
          font-size: 1rem;
          padding: 10px 16px;
          border-radius: 20px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .badge-info {
          background: linear-gradient(135deg, #17a2b8, #20c997);
          color: white;
        }

        .badge-warning {
          background: linear-gradient(135deg, #ffc107, #fd7e14);
          color: #212529;
        }

        .badge-success {
          background: linear-gradient(135deg, #28a745, #20c997);
          color: white;
        }

        .card {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          border: none;
          border-radius: 15px;
          transition: all 0.3s ease;
          background: white;
          overflow: hidden;
        }

        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
        }

        .card-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-bottom: none;
          padding: 20px 25px;
          border-radius: 15px 15px 0 0;
        }

        .card-header h5 {
          margin: 0;
          font-weight: 600;
          font-size: 1.2rem;
        }

        .card-body {
          padding: 25px;
        }

        .form-group {
          margin-bottom: 25px;
        }

        .form-group label {
          font-size: 0.95rem;
          color: #495057;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .form-control {
          border: 2px solid #e9ecef;
          border-radius: 10px;
          padding: 12px 15px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background-color: #fff;
        }

        .form-control:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          background-color: #fff;
        }

        .input-group-text {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: 2px solid #667eea;
          border-left: none;
          font-weight: 600;
          border-radius: 0 10px 10px 0;
        }

        .btn {
          border-radius: 10px;
          padding: 12px 25px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          transition: all 0.3s ease;
          border: none;
        }

        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
          background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
        }

        .btn-secondary {
          background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-secondary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
          background: linear-gradient(135deg, #5a6268 0%, #3d4345 100%);
        }

        .progress {
          border-radius: 10px;
          height: 12px;
          background-color: #e9ecef;
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .progress-bar {
          border-radius: 10px;
          transition: all 0.6s ease;
        }

        .bg-success {
          background: linear-gradient(90deg, #28a745, #20c997) !important;
        }

        .bg-danger {
          background: linear-gradient(90deg, #dc3545, #fd7e14) !important;
        }

        .font-weight-medium {
          font-weight: 500;
        }

        .text-success {
          color: #28a745 !important;
          font-weight: 600;
        }

        .text-danger {
          color: #dc3545 !important;
          font-weight: 600;
        }

        .alert-info {
          background: linear-gradient(135deg, #d1ecf1, #bee5eb);
          border: none;
          border-radius: 10px;
          color: #0c5460;
          padding: 20px;
          font-weight: 500;
        }

        h2 {
          color: #2d3748;
          font-weight: 700;
          margin-bottom: 5px;
        }

        h6 {
          color: #4a5568;
          font-weight: 600;
          margin-bottom: 15px;
          font-size: 1.1rem;
        }

        .text-muted {
          color: #718096 !important;
          font-size: 0.9rem;
        }

        p {
          color: #4a5568;
          line-height: 1.6;
          margin-bottom: 12px;
        }

        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }

        /* Header styling */
        .maintenance-header {
          background: white;
          border-radius: 15px;
          padding: 25px;
          margin-bottom: 30px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        }

        /* Progress section styling */
        .progress-section {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .progress-item {
          background: white;
          border-radius: 10px;
          padding: 15px;
          margin-bottom: 15px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .progress-item:last-child {
          margin-bottom: 0;
        }

        @media (max-width: 768px) {
          .container {
            padding-left: 15px;
            padding-right: 15px;
            padding-top: 15px;
          }

          .maintenance-header {
            padding: 20px;
            margin-bottom: 20px;
          }

          .maintenance-header .d-flex {
            flex-direction: column;
            align-items: flex-start !important;
          }

          .maintenance-header .badge {
            margin-top: 15px;
            align-self: flex-end;
          }

          h2 {
            font-size: 1.5rem;
          }

          .card-body {
            padding: 20px;
          }

          .card-header {
            padding: 15px 20px;
          }

          .btn {
            width: 100%;
            margin-bottom: 10px;
          }

          .btn-secondary {
            width: auto;
            margin-bottom: 0;
          }
        }

        @media (max-width: 576px) {
          .card-header h5 {
            font-size: 1rem;
          }

          .form-control {
            padding: 10px 12px;
          }

          .btn {
            padding: 10px 20px;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
}

export default withTranslation()(MaintenanceDetail);
